import type { CaptureResult } from 'posthog-js';
import {
  EMAIL_ATTRIBUTION_QUERY_PARAMETERS,
  EMAIL_ATTRIBUTION_SENSITIVE_QUERY_PARAMETERS,
  EMAIL_ATTRIBUTION_UTM_PARAMETERS,
  isRecognizedKorEmailAttributionQuery,
} from './email-notification-attribution';

const EMAIL_ATTRIBUTION_CONTEXT_FIELDS = [
  'email_attribution_context',
  'attribution_context',
] as const;

/** Direct telemetry fields that Rollbar must scrub when values are not embedded in a URL. */
export const EMAIL_ATTRIBUTION_TELEMETRY_FIELD_NAMES = [
  ...EMAIL_ATTRIBUTION_UTM_PARAMETERS,
  'email_link_token',
  ...EMAIL_ATTRIBUTION_CONTEXT_FIELDS,
] as const;

/** Secret field names scrubbed even when a telemetry object is not a complete email arrival. */
export const EMAIL_ATTRIBUTION_SENSITIVE_FIELD_NAMES = [
  'email_link_token',
  ...EMAIL_ATTRIBUTION_CONTEXT_FIELDS,
] as const;

const EMAIL_ATTRIBUTION_TELEMETRY_FIELD_SET = new Set<string>(
  EMAIL_ATTRIBUTION_TELEMETRY_FIELD_NAMES,
);
const EMAIL_ATTRIBUTION_SENSITIVE_FIELD_SET = new Set<string>(
  EMAIL_ATTRIBUTION_SENSITIVE_FIELD_NAMES,
);
const EMAIL_ATTRIBUTION_UTM_FIELD_SET = new Set<string>(EMAIL_ATTRIBUTION_UTM_PARAMETERS);
const EMAIL_ATTRIBUTION_QUERY_FIELD_SET = new Set<string>(EMAIL_ATTRIBUTION_QUERY_PARAMETERS);
const EMAIL_ATTRIBUTION_URL_SENSITIVE_PARAMETER_SET = new Set<string>(
  [...EMAIL_ATTRIBUTION_SENSITIVE_QUERY_PARAMETERS, ...EMAIL_ATTRIBUTION_CONTEXT_FIELDS].map(
    (parameter) => parameter.toLowerCase(),
  ),
);
const REDACTED_EMAIL_ATTRIBUTION_VALUE = '[notification attribution redacted]';

/** Removes secrets from URLs, and removes UTMs only for a recognized KOR email arrival. */
export function redactEmailAttributionFromUrl(
  value: string,
  depth = 0,
  redactAllAttributionFields = false,
): string {
  try {
    const isAbsoluteUrl = /^[a-z][a-z\d+.-]*:\/\//i.test(value);
    const url = new URL(value, 'https://redaction.invalid');
    const shouldRedactAttributionUtms =
      redactAllAttributionFields ||
      isRecognizedKorEmailAttributionQuery(url.searchParams, url.pathname);
    let changed = false;
    Array.from(url.searchParams.entries()).forEach(([parameter, parameterValue]) => {
      const normalizedParameter = parameter.toLowerCase();
      if (
        EMAIL_ATTRIBUTION_URL_SENSITIVE_PARAMETER_SET.has(normalizedParameter) ||
        (shouldRedactAttributionUtms && EMAIL_ATTRIBUTION_UTM_FIELD_SET.has(normalizedParameter))
      ) {
        url.searchParams.delete(parameter);
        changed = true;
        return;
      }
      if (depth >= 3) return;
      const redactedParameterValue = redactEmailAttributionFromUrl(
        parameterValue,
        depth + 1,
        redactAllAttributionFields,
      );
      if (redactedParameterValue !== parameterValue) {
        url.searchParams.set(parameter, redactedParameterValue);
        changed = true;
      }
    });
    if (!changed) return value;
    if (isAbsoluteUrl) return url.toString();
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    let decodedValue = value;
    try {
      decodedValue = decodeURIComponent(value);
    } catch {
      // A malformed encoded URL is redacted below only when it still exposes a known key.
    }
    const fallbackFieldSet = redactAllAttributionFields
      ? new Set([
          ...Array.from(EMAIL_ATTRIBUTION_URL_SENSITIVE_PARAMETER_SET),
          ...Array.from(EMAIL_ATTRIBUTION_UTM_FIELD_SET),
        ])
      : EMAIL_ATTRIBUTION_URL_SENSITIVE_PARAMETER_SET;
    const containsAttributionKey = Array.from(fallbackFieldSet).some((parameter) =>
      decodedValue.toLowerCase().includes(parameter),
    );
    return containsAttributionKey ? '[notification attribution URL redacted]' : value;
  }
}

function hasRecognizedTelemetryAttribution(record: Record<string, unknown>): boolean {
  if (
    Object.keys(record).some((key) =>
      EMAIL_ATTRIBUTION_CONTEXT_FIELDS.includes(
        key.toLowerCase() as (typeof EMAIL_ATTRIBUTION_CONTEXT_FIELDS)[number],
      ),
    )
  ) {
    return true;
  }
  const parameters = new URLSearchParams();
  Object.entries(record).forEach(([key, item]) => {
    const normalizedKey = key.toLowerCase();
    if (EMAIL_ATTRIBUTION_QUERY_FIELD_SET.has(normalizedKey) && typeof item === 'string') {
      parameters.set(normalizedKey, item);
    }
  });
  return isRecognizedKorEmailAttributionQuery(parameters);
}

function redactTelemetryValue(
  value: unknown,
  redactAllAttributionFields: boolean,
  seen = new WeakSet<object>(),
): unknown {
  if (typeof value === 'string') {
    return redactEmailAttributionFromUrl(value, 0, redactAllAttributionFields);
  }
  if (!value || typeof value !== 'object' || value instanceof Date) return value;
  if (seen.has(value)) return value;
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      value[index] = redactTelemetryValue(item, redactAllAttributionFields, seen);
    });
    return value;
  }

  const record = value as Record<string, unknown>;
  const hasRecognizedAttribution = hasRecognizedTelemetryAttribution(record);
  Object.entries(record).forEach(([key, item]) => {
    const normalizedKey = key.toLowerCase();
    const shouldRedactField = redactAllAttributionFields
      ? EMAIL_ATTRIBUTION_TELEMETRY_FIELD_SET.has(normalizedKey)
      : EMAIL_ATTRIBUTION_SENSITIVE_FIELD_SET.has(normalizedKey) ||
        (hasRecognizedAttribution && EMAIL_ATTRIBUTION_UTM_FIELD_SET.has(normalizedKey));
    record[key] = shouldRedactField
      ? REDACTED_EMAIL_ATTRIBUTION_VALUE
      : redactTelemetryValue(item, redactAllAttributionFields, seen);
  });
  return value;
}

/** PostHog before-send hook that redacts attributed URLs before any browser event is transmitted. */
export function redactPostHogEmailAttribution(event: CaptureResult | null): CaptureResult | null {
  if (!event) return null;
  redactTelemetryValue(event.properties, false);
  redactTelemetryValue(event.$set, false);
  redactTelemetryValue(event.$set_once, false);
  return event;
}

/** Rollbar transform that strips attributed URLs from payload, telemetry, and referrer fields. */
export function redactRollbarEmailAttribution(payload: Record<string, unknown>): void {
  redactTelemetryValue(payload, true);
}
